<template>
  <div
    class="dropdown icon-select"
    :style="{ '--icon-accent': accentColor || defaultAccent }"
  >
    <button
      ref="toggle"
      type="button"
      class="icon-select-trigger"
      data-toggle="dropdown"
      aria-haspopup="true"
      aria-expanded="false"
      title="Choose prompt icon"
    >
      <i :class="['fas', modelValue || defaultIcon]"></i>
      <span class="icon-select-badge">
        <i class="fas fa-chevron-down"></i>
      </span>
    </button>
    <div class="dropdown-menu icon-select-menu">
      <button
        v-for="option in options"
        :key="option.value"
        type="button"
        class="dropdown-item icon-swatch-item"
        :class="{ active: option.value === modelValue }"
        :title="option.label"
        @click="select(option.value)"
      >
        <i :class="['fas', option.value]"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { DEFAULT_PROMPT_ICON, PROMPT_ICON_OPTIONS } from "./promptIcons";

export default {
  name: "prompt-icon-picker",
  emits: ["update:modelValue"],
  props: {
    modelValue: {
      type: String,
      default: null,
    },
    accentColor: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      options: PROMPT_ICON_OPTIONS,
      defaultIcon: DEFAULT_PROMPT_ICON,
      defaultAccent: "var(--primary)",
    };
  },
  methods: {
    select(value) {
      this.$emit("update:modelValue", value);
      // Bootstrap's own dropdown JS (already loaded app-wide) handles
      // open/close-on-outside-click; explicitly hide it here too since a
      // plain <button> click inside .dropdown-menu isn't guaranteed to
      // bubble the same way an <a> does.
      window.$(this.$refs.toggle).dropdown("hide");
    },
  },
};
</script>

<style scoped lang="scss">
.icon-select-trigger {
  position: relative;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;

  // No .dropdown-toggle class on this trigger (see template), so there's no
  // default Bootstrap caret to remove, but keep this as a guard in case
  // that class is ever reintroduced.
  &::after {
    display: none;
  }

  i {
    font-size: 16px;
    color: var(--icon-accent);
    transition: transform 0.15s ease;
  }

  &:hover,
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.08);
  }

  &:hover i,
  &:focus i {
    transform: scale(1.1);
  }

  &:hover .icon-select-badge,
  &:focus .icon-select-badge {
    background: var(--light);
  }
}

// A small avatar-style "edit" badge in the corner — signals the icon is
// clickable/changeable without bringing back Bootstrap's plain caret.
.icon-select-badge {
  position: absolute;
  bottom: -2px;
  right: -2px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--white);
  border: 1px solid var(--light);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;

  i {
    font-size: 7px;
    color: var(--gray-dark);
  }
}

.icon-select-menu {
  // Bootstrap's own .dropdown-menu { display: none; } must stay in effect
  // until its JS adds .show — only override display once that's present,
  // otherwise this grid rule would keep the menu visible permanently.
  &.show {
    display: grid;
  }
  grid-template-columns: repeat(6, 1fr);
  gap: 4px;
  padding: 8px;
  min-width: 232px;
  max-height: 220px;
  overflow-y: auto;
  z-index: 1060;
}

.icon-swatch-item {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  padding: 0;
  color: var(--gray-dark);

  &:hover {
    background: var(--light);
    color: var(--icon-accent);
  }

  &.active {
    background: var(--light);
    color: var(--icon-accent);
    box-shadow: 0 0 0 1px var(--icon-accent);
  }
}
</style>
